import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ViewChild, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DownloadRequest } from '../../../models/download-filter.model';
import { AuthPolicies } from '../../../auth/auth-policies';

import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button-group';
import '@maersk-global/mds-components-core/mc-button-group-item';
import { DamageCodeAdvanceFilterComponent } from '../damage-code-advance-filter/damage-code-advance-filter.component';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { DamageCodeFilterForm } from '../../../form-model/damage-code-filter.form.model';
import { ReplaySubject, take } from 'rxjs';
import { DamageCodeSearchTextForm } from '../../../form-model/damage-code-search-text.form.model';
import { DamageCodeFilter } from '../../../models/damage-code-filter.model';
import { DamageCodeService } from '../../../services/damage-code.service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { environment } from '../../../../environments/environment';
import { ToastrService } from '../../../services/toastr-service';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';

type DamageCodeTagged = { id: string; label: string };
type FilterValue = string | string[] | DamageCodeTagged[] | boolean;

@Component({
  selector: 'app-damage-code-filters',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, DamageCodeAdvanceFilterComponent, TagWithOverflowComponent],
  templateUrl: './damage-code-filter.component.html',
  styleUrl: './damage-code-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DamageCodeFilterComponent implements AfterViewInit {
  isDamageCodeAddEnabled = signal(this.authPolicies.canAddDamageCode && environment.Features.EnableDamageCode);
  showAdvanceFilters: boolean = false;
  readonly screenWidth = window.innerWidth;

  // Damage types mapping from code to display label
  private readonly damageTypesMap: Record<string, string> = {
    structural: '0 - Structural',
    reefer_unit: '1 - ReeferUnit',
    genset: '2 - Genset',
  };

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    damageCodeFilterForm: FormGroup<DamageCodeFilterForm>;
    damageCodeSearchTextForm: FormGroup<DamageCodeSearchTextForm>;
  }>;

  savedFilter!: FormGroup<DamageCodeFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  markCompleteApiCallStatus: APICallStatus = 'NOT_STARTED';
  @Output() markAsCompleteSuccess = new EventEmitter<boolean>();
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isDamageCodesList: boolean = true;
  advancedFilterLoading = false;
  @ViewChild(DamageCodeAdvanceFilterComponent) advFilter!: DamageCodeAdvanceFilterComponent;
  isDownload: boolean = false;

  constructor(
    public authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly damageCodeService: DamageCodeService,
  ) {}

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private setFilterFormValue() {
    const filterValue = this.damageCodeService.getDamageCodeFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.damageCodeFilterForm.setValue(filterValue);
      this.updateSelectedFiltersSection();
    }
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.controls.damageCodeFilterForm?.controls.applyDCFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.damageCodeService.setDamageCodeFilterFormValue(this.filterFormGroup.controls.damageCodeFilterForm.getRawValue());
      } else {
        this.damageCodeService.setDamageCodeFilterFormValue(null);
      }
    });
    this.filterFormGroup.controls.damageCodeSearchTextForm.controls.appliedDCSearchTextFilter?.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }

  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1, itemValue = ''): void {
    switch (taggedFilter.key) {
      case 'Damage Code - Description': {
        // remove by index from the underlying form control (which stores ids)
        const currentIds = this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value;
        if (Array.isArray(currentIds) && itemNumber >= 0 && itemNumber < currentIds.length) {
          const updatedIds = currentIds.filter((_: any, idx: number) => idx !== itemNumber);
          this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.setValue(
            updatedIds.length ? updatedIds : null,
          );
        }
        break;
      }

      case 'Damage Type':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let taggedDamageType = taggedFilter.value as string[];
          const updatedDamageType: string[] = taggedDamageType.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.setValue(
            updatedDamageType.length ? updatedDamageType : null,
          );
        }
        break;

      default:
        break;
    }
    this.filterFormGroup.controls.damageCodeFilterForm.controls.applyDCFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal() {
    this.advancedFilterLoading = true;
    // open modal first so child component is created, then ensure the child's options are loaded
    this.showAdvanceFilters = true;
    // save immediately so tests and UI can rely on the saved state
    this.saveCurrentFilters();
    this.ensureAdvanceOptionsLoaded()
      .then(() => {
        this.advancedFilterLoading = false;
      })
      .catch(() => {
        this.advancedFilterLoading = false;
      });
  }

  private async ensureAdvanceOptionsLoaded(): Promise<void> {
    // wait for the child component to be available (it is created when modal is open)
    const maxAttempts = 20;
    let attempts = 0;
    while (!this.advFilter && attempts < maxAttempts) {
      // small delay
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 50));
      attempts++;
    }
    if (this.advFilter && typeof this.advFilter.loadAllDamageCodeUniqueOptions === 'function') {
      // call the child's loader (it's async)
      // eslint-disable-next-line no-unsafe-finally
      return this.advFilter.loadAllDamageCodeUniqueOptions();
    }
  }

  advancedFilterHasOpened() {
    this.advancedFilterLoading = false;
  }

  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.damageCodeFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.damageCodeFilterForm.controls.applyDCFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private saveCurrentFilters(): void {
    this.savedFilter = this.formBuilder.group({
      damageCodeDescriptionList: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value,
      ),
      damageTypesList: this.formBuilder.control<string[] | null>(
        this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.value,
      ),
      applyDCFilter: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.damageCodeFilterForm.controls.applyDCFilter.value,
      ),
    });
  }

  updateSelectedFiltersSection() {
    const rawCodes = this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value;
    if (rawCodes && Array.isArray(rawCodes) && rawCodes.length > 0) {
      // map ids like 'CODE__index' back to a friendly label using unique code list from service
      this.damageCodeService
        .getUniqueDamageCodes()
        .pipe(take(1))
        .subscribe({
          next: (list) => {
            const mapped = rawCodes.map((id: string) => {
              const parts = (id || '').toString().split('__');
              const idx = parts.length > 1 ? Number.parseInt(parts[1], 10) : -1;
              const code = parts[0];
              let found = undefined;
              if (!Number.isNaN(idx) && idx >= 0 && idx < list.length) {
                found = list[idx];
              }
              found ??= list.find((x) => x.code === code);
              return found ? `${found.code} - ${found.description}` : code;
            });
            this.markAsTagged('Damage Code - Description', mapped);
          },
          error: () => {
            this.markAsTagged('Damage Code - Description', rawCodes);
          },
        });
    } else {
      this.markAsTagged('Damage Code - Description', rawCodes);
    }
    this.markAsTagged('Damage Type', this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.value);
    this.updateStatusTag();
  }

  private updateStatusTag() {
    const filters: DamageCodeFilter = {};
    if (this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value) {
      filters.damageCodeDescriptionList = this.filterFormGroup.controls.damageCodeFilterForm.controls.damageCodeDescriptionList.value;
    }
    if (this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.value) {
      filters.damageTypesList = this.filterFormGroup.controls.damageCodeFilterForm.controls.damageTypesList.value;
    }
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== false && newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string) {
    return getSharedFilterValue(this.taggedFilters, key);
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    switch (taggedFilter.key) {
      case 'Damage Code - Description': {
        let damageCodeDescriptionList = taggedFilter.value as string[];
        return itemNumber >= 0 && itemNumber < damageCodeDescriptionList.length
          ? damageCodeDescriptionList[itemNumber]
          : damageCodeDescriptionList;
      }
      case 'Damage Type': {
        let damageTypeList = taggedFilter.value as string[];
        if (itemNumber >= 0 && itemNumber < damageTypeList.length) {
          const code = damageTypeList[itemNumber];
          // Map the code to its display description
          return this.damageTypesMap[code] || code;
        }
        // For all items, map each code to its display description
        return damageTypeList.map((code) => this.damageTypesMap[code] || code);
      }
      default:
        return '';
    }
  }

  getTaggedDamageCode(): string[] {
    const damageCode = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Damage Code - Description');
    return damageCode && Array.isArray(damageCode.value) ? (damageCode.value as string[]) : [];
  }

  unselectDamageCodeByValue() {
    return (value: string) => {
      const taggedFilter = this.taggedFilters.find((f) => f.key === 'Damage Code - Description');
      if (!taggedFilter) return;
      const index = this.getTaggedDamageCode().indexOf(value);
      if (index >= 0) {
        this.unselectSpecificFilter(taggedFilter, index);
      }
    };
  }

  getTaggedDamageType(): string[] {
    const damageType = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Damage Type');
    if (!damageType || !Array.isArray(damageType.value)) return [];

    const arr = damageType.value as any[];
    return arr.map((item) => {
      let code: string;
      if (typeof item === 'string') {
        code = item;
      } else {
        code = item?.label ?? item?.code ?? String(item);
      }
      // Map the code to its display description
      return this.damageTypesMap[code] || code;
    });
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onDownload() {
    this.isDownload = true;
    const request: DownloadRequest = { columns: [], filters: [] };
    this.damageCodeService.downloadToExcel(request).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('damage-code'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }
}
